import React, { useState, useEffect } from 'react';
import { Container, Paper, Typography, TextField, Button, Box, Alert, Divider, CircularProgress, Chip } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { C } from '../theme';

const Profile: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError('Nova lozinka i potvrda lozinke se ne poklapaju');
      return;
    }

    if (newPassword.length < 6) {
      setError('Nova lozinka mora imati najmanje 6 karaktera');
      return;
    }

    setFormLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/auth/change-password', {
        oldPassword,
        newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Lozinka je uspešno promenjena!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Greška pri promeni lozinke. Pokušajte ponovo.');
      }
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ pt: '80px', pb: 6, backgroundColor: C.bg, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: C.gold }} />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Box
      sx={{
        pt: '110px',
        pb: 8,
        minHeight: '100vh',
        backgroundColor: C.bg,
        backgroundImage: 'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(243,192,21,0.10), transparent 70%)',
      }}
    >
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, backgroundColor: C.surface, borderRadius: 4, border: `1px solid ${C.line}` }}>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: C.white, textAlign: 'center' }}>
            Moj profil
          </Typography>
          <Typography variant="body1" sx={{ color: C.muted, mb: 4, textAlign: 'center' }}>
            Pregledajte vaše podatke i promenite lozinku.
          </Typography>

          {/* User Information Section */}
          <Box sx={{ mb: 4, p: 3, backgroundColor: C.surface2, borderRadius: 3, border: `1px solid ${C.line}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: C.white }}>
              Informacije o korisniku
            </Typography>

            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: '120px', color: C.muted }}>
                  Ime i prezime:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: C.white }}>
                  {user.full_name || 'Nije postavljeno'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: '120px', color: C.muted }}>
                  Email:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: C.white }}>
                  {user.email || 'Nije postavljeno'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: '120px', color: C.muted }}>
                  Tip korisnika:
                </Typography>
                <Chip
                  label={
                    user.userType === 'land_owner' ? 'Vlasnik zemljišta' :
                    user.userType === 'business_owner' ? 'Vlasnik biznisa' :
                    'Investitor'
                  }
                  sx={{ fontWeight: 600, backgroundColor: C.goldSoft, color: '#F6CB4B', border: `1px solid rgba(243,192,21,0.4)` }}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 4, borderColor: C.line }} />

          {/* Password Change Section */}
          <Box sx={{ p: 3, backgroundColor: C.surface2, borderRadius: 3, border: `1px solid ${C.line}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: C.white }}>
              Promena lozinke
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handlePasswordChange} sx={{ display: 'grid', gap: 3 }}>
              <TextField
                label="Trenutna lozinka"
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
                fullWidth
                variant="outlined"
              />

              <TextField
                label="Nova lozinka"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                fullWidth
                variant="outlined"
                helperText="Lozinka mora imati najmanje 6 karaktera"
              />

              <TextField
                label="Potvrda nove lozinke"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                variant="outlined"
              />

              <Button type="submit" variant="contained" size="large" disabled={formLoading} sx={{ py: 1.5, fontSize: '1.05rem' }}>
                {formLoading ? <CircularProgress size={24} sx={{ color: C.onGold }} /> : 'Promeni lozinku'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Profile;
